import { createTheme, Theme } from '@mui/material/styles';
import { getColors, spacing, typography, type ThemeMode } from './tokens';

// Extend the theme to include custom colors
declare module '@mui/material/styles' {
  interface Palette {
    primaryLight: {
      main: string;
    };
  }

  interface PaletteOptions {
    primaryLight?: {
      main: string;
    };
  }

  interface TypeText {
    navbar: string;
  }
}

// Theme factory function that creates themes for different modes
export const createEventbildTheme = (mode: ThemeMode = 'light'): Theme => {
  const colors = getColors(mode);

  return createTheme({
    palette: {
      mode,
      primary: {
        main: colors.primary,
      },
      secondary: {
        main: colors.secondary,
      },
      primaryLight: {
        main: colors.primaryLight,
      },
      background: {
        default: colors.background,
        paper: colors.surface,
      },
      text: {
        primary: colors.text.primary,
        secondary: colors.text.secondary,
        disabled: colors.text.disabled,
        navbar: colors.text.navbar,
      },
      error: {
        main: colors.error,
      },
      warning: {
        main: colors.warning,
      },
      info: {
        main: colors.info,
      },
      success: {
        main: colors.success,
      },
      divider: colors.divider,
    },
    typography: {
      fontFamily: typography.fontFamily,
      fontWeightLight: typography.weights.light,
      fontWeightRegular: typography.weights.regular,
      fontWeightMedium: typography.weights.medium,
      fontWeightBold: typography.weights.bold,
      h1: {
        fontSize: '2rem',
        fontWeight: typography.weights.light,
      },
      h2: {
        fontSize: '1.8rem',
        fontWeight: typography.weights.light,
      },
      h3: {
        fontSize: '1.3rem',
        fontWeight: typography.weights.light,
      },
      h4: {
        fontSize: '1.1rem',
        fontWeight: typography.weights.light,
      },
      body1: {
        fontSize: 20,
      },
    },
    shape: {
      borderRadius: '0.3rem',
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          // Global link styling for regular content (excluding AppBar/Toolbar)
          'a:not(.MuiAppBar-root a):not(.MuiToolbar-root a)': {
            color: colors.primary,
            textDecoration: 'none',
            '&:hover': {
              textDecoration: 'underline',
              color: mode === 'dark' ? colors.primaryLight : colors.primary,
            },
            '&:visited': {
              color: colors.primary,
            },
            '&:focus': {
              color: colors.primary,
              outline: `2px solid ${colors.primary}`,
              outlineOffset: '2px',
            },
          },
          // Global styles to handle anchor tags containing buttons and menu items
          'a:has(.MuiButton-root)': {
            color: 'inherit !important',
            textDecoration: 'none !important',
          },
          'a:has(.MuiMenuItem-root)': {
            color: 'inherit !important',
            textDecoration: 'none !important',
          },
          // Fallback for browsers that don't support :has()
          'a .MuiButton-root': {
            color: `${colors.text.primary} !important`,
          },
          'a .MuiMenuItem-root': {
            color: `${colors.text.primary} !important`,
          },
        },
      },
      MuiButton: {
        defaultProps: {
          style: {
            marginRight: '1rem',
          },
        },
        styleOverrides: {
          root: {
            color: `${colors.text.primary} !important`, // Force color with !important for Link components
            textDecoration: 'none',
            '&:hover': {
              backgroundColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)',
              textDecoration: 'underline',
            },
            '&:visited': {
              color: `${colors.text.primary} !important`,
            },
            '&:focus': {
              color: `${colors.text.primary} !important`,
            },
            // Specifically target when used with Link component
            '&.MuiButton-root': {
              color: `${colors.text.primary} !important`,
            },
          },
          contained: {
            color: `${colors.text.navbar} !important`, // White text in normal state
            backgroundColor: colors.primary, // Ensure primary background
            '&:hover': {
              color: `${colors.text.primary} !important`, // Dark text on hover (inverted)
              backgroundColor: colors.background, // Light background on hover for contrast
              // Additional specificity for hover state
              '&.MuiButton-contained': {
                color: `${colors.text.primary} !important`,
              },
            },
            '&:focus': {
              color: `${colors.text.navbar} !important`,
            },
            '&:visited': {
              color: `${colors.text.navbar} !important`,
            },
            // More specific selector to override Material-UI defaults
            '&.MuiButton-contained': {
              color: `${colors.text.navbar} !important`,
            },
          },
          outlined: {
            color: `${colors.text.primary} !important`, // Dark text in normal state
            borderColor: colors.primary,
            backgroundColor: 'transparent', // Transparent background for outlined buttons
            '&:hover': {
              color: `${colors.text.navbar} !important`, // White text on hover
              backgroundColor: colors.primary, // Primary background on hover
              borderColor: colors.primary,
            },
            '&:focus': {
              color: `${colors.text.primary} !important`,
            },
            '&:visited': {
              color: `${colors.text.primary} !important`,
            },
          },
        },
        variants: [
          {
            props: { variant: 'outlined' },
            style: {
              color: colors.text.primary,
            },
          },
        ],
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: colors.navbar,
            '& .MuiButton-root': {
              color: `${colors.text.navbar} !important`, // Force navbar color on AppBar buttons
              '&:hover': {
                color: `${colors.text.navbar} !important`,
              },
              '&:focus': {
                color: `${colors.text.navbar} !important`,
              },
              '&:visited': {
                color: `${colors.text.navbar} !important`,
              },
            },
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            color: colors.text.primary,
            '&:hover': {
              backgroundColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)',
            },
            '&.Mui-selected': {
              backgroundColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.08)',
            },
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            backgroundColor: `${colors.navbar} !important`, // Use same background as AppBar with !important
            color: colors.text.primary,
          },
          list: {
            backgroundColor: `${colors.navbar} !important`, // Also target the list component
            color: colors.text.primary,
          },
        },
      },
    },
    spacing: spacing.unit,
  });
};

// Pre-built theme instances
export const lightTheme = createEventbildTheme('light');
export const darkTheme = createEventbildTheme('dark');

// Default export is the light theme for backward compatibility
export default lightTheme;
